import React from 'react';
import { ImageLogic } from './Image.logic';
import { SimpleGalleryModal } from '@wix/simple-gallery';
import { dataHooks } from 'root/dataHooks';
import { classes as ImageClasses, st } from './Image.st.css';
import { WowImage, ImageResizeOptions, Badge, BadgePriority, Text } from 'wix-ui-tpa/cssVars';
import type { ItemImage } from 'root/apiTypes';
import { useImageBi } from './image.bi';
import { useTranslation } from '@wix/yoshi-flow-editor';

interface ImageProps<T extends ItemImage> {
  image: T;
  additionalImages: T[];
  imageClassName: string;
  dataHook: string;
  alt: string;
  resize?: ImageResizeOptions;
  shouldDisableExpand?: boolean;
}

export const Image = <T extends ItemImage>({
  image,
  additionalImages,
  alt,
  dataHook,
  imageClassName,
  resize = ImageResizeOptions.cover,
  shouldDisableExpand,
}: ImageProps<T>) => {
  const { t } = useTranslation();
  const { onNext, onPrevious, onClose, onExpand } = useImageBi();
  const {
    allImages,
    onImageClick,
    displayedImage,
    shouldShowMultiImagesIndication,
    isImageClickable,
    isGalleryOpen,
    setIsGalleryOpen,
    isMobile,
    onImageHover,
    onImageLeave,
  } = ImageLogic<ItemImage>({
    additionalImages,
    image,
    onExpand,
    shouldDisableExpand,
  });
  return (
    <>
      <div
        aria-label={t('component.image.modal.click.aria-label')}
        data-hook={dataHooks.image.clickableImage(dataHook)}
        className={`${imageClassName} ${
          isImageClickable ? ImageClasses.imageWrapperClickable : ImageClasses.imageWrapperNotClickable
        }`}
        onClick={onImageClick}
        onMouseEnter={onImageHover}
        onMouseLeave={onImageLeave}
      >
        <WowImage
          shouldUseLQIP
          alt={alt}
          data-hook={dataHooks.image.image(dataHook)}
          resize={resize}
          sourceHeight={displayedImage.sourceHeight}
          sourceWidth={displayedImage.sourceWidth}
          src={displayedImage.src}
        />

        {shouldShowMultiImagesIndication && (
          <Badge
            className={ImageClasses.badge}
            data-hook={dataHooks.image.badge(dataHook)}
            priority={BadgePriority.default}
          >
            <Text className={st(ImageClasses.badgeText)} data-hook={dataHooks.image.badgeText(dataHook)}>
              1/{allImages.length}
            </Text>
          </Badge>
        )}
      </div>

      <SimpleGalleryModal
        arrowsClassName={ImageClasses.arrows}
        closeButtonAriaLabel={t('component.image.modal.close.aria-label')}
        dataHook={dataHooks.image.simpleGalleryModal(dataHook)}
        images={allImages}
        isMobile={isMobile ?? false}
        isOpen={isGalleryOpen}
        nextButtonAriaLabel={t('component.image.modal.next.aria-label')}
        previousButtonAriaLabel={t('component.image.modal.previous.aria-label')}
        setIsOpen={setIsGalleryOpen}
        biFunctions={{
          onNext,
          onPrevious,
          onClose,
        }}
      />
    </>
  );
};
